// Renders a forum post's body. Plain text only, on purpose: React escapes
// everything by default, so there's no HTML injection surface here and
// none gets added. Two conveniences on top of that —
//   - bare http(s) links become real links (rel="noopener nofollow", so a
//     public board full of stranger-posted links can't leak referrers or
//     pass SEO juice to spam), and
//   - lines starting with ">" render as a quote, the one bit of markup
//     people reflexively use on message boards.
// If richer formatting is ever wanted, add a real sanitising markdown
// renderer — do not start hand-rolling dangerouslySetInnerHTML.

const SPLIT_URL = /(https?:\/\/[^\s<]+)/g;

function isUrl(part: string): boolean {
  return /^https?:\/\//i.test(part);
}

export function ForumText({ text }: { text: string }) {
  const lines = text.split("\n");
  return (
    <div className="forum-text">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="forum-text__gap" />;
        const isQuote = line.trimStart().startsWith(">");
        const content = isQuote ? line.replace(/^\s*>\s?/, "") : line;
        const parts = content.split(SPLIT_URL);
        return (
          <p key={i} className={isQuote ? "forum-text__quote" : "forum-text__line"}>
            {parts.map((part, j) =>
              isUrl(part) ? (
                <a key={j} href={part} target="_blank" rel="noopener noreferrer nofollow" className="forum-text__link">
                  {part}
                </a>
              ) : (
                <span key={j}>{part}</span>
              )
            )}
          </p>
        );
      })}
    </div>
  );
}

/** Deterministic hue from a name so avatars differ without any per-user
 *  state — same trick the chat avatars use. */
export function avatarHue(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
}

export function ForumAvatar({ name, isWallet }: { name: string; isWallet: boolean }) {
  const label = (name || "anon").slice(0, 2).toUpperCase();
  return (
    <span
      className="forum-avatar"
      style={{ background: `hsl(${avatarHue(name || "anon")} 60% 32%)` }}
      title={isWallet ? `${name} (WAX account)` : name || "anon"}
    >
      {label}
    </span>
  );
}
